/**
 * @file CustomShippingFormScreen.tsx
 *
 * Routed wrapper around {@link CustomShippingForm} for the
 * `/shipping/custom-shipping/new` and `/:zoneId/edit` routes. Seeds the form from
 * the store (cache-first; fetches the shipping data when missing, e.g. a refresh
 * or deep link), shows loading/load-error states, and owns persistence: on save it
 * creates or updates the zone, replaces its regions, and applies the method diff
 * (add/update/delete) via the store, toasts the save outcome, then navigates back
 * to the overview. The seed is memoized so the form's controlled state isn't reset
 * on every render.
 */

import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Loader } from '@group.one/react-gravity';
import { FormattedMessage, useIntl } from 'react-intl';
import { adminMessages } from '../../i18n/messages';
import { notifyError, notifySuccess } from '../../feedback/toast';
import { useShippingStore } from '../../stores/shipping.store';
import { useSettingsStore } from '../../stores/settings.store';
import type {
  ShippingMethod,
  ShippingZoneLocation,
} from '../../api/shop-types';
import { EmptyState } from '../../components/EmptyState';
import { removeAllRegionsLocation } from '../shipping-region-utils';
import { useLocalizedContinents } from '../use-localized-continents';
import { CustomShippingForm, type CustomShippingFormMode } from './CustomShippingForm';
import {
  buildMethodSettings,
  deriveCustomShippingFields,
  diffMethods,
} from './custom-shipping-form-utils';
import type { CustomShippingFormFields } from './custom-shipping-form-types';

/** Stable empties so the seed memo doesn't recompute on every render. */
const EMPTY_METHODS: ShippingMethod[] = [];
const EMPTY_LOCATIONS: ShippingZoneLocation[] = [];

interface CustomShippingFormScreenProps {
  mode: CustomShippingFormMode;
}

/**
 * Renders the routed custom-shipping form for the given mode.
 *
 * @param props - The form mode (create/edit).
 *
 * @returns The zone form screen (with loading/error fallbacks).
 */
export function CustomShippingFormScreen({ mode }: CustomShippingFormScreenProps) {
  const intl = useIntl();
  const navigate = useNavigate();
  const { zoneId } = useParams();

  const zones = useShippingStore((s) => s.zones);
  const zoneMethods = useShippingStore((s) => s.zoneMethods);
  const zoneLocations = useShippingStore((s) => s.zoneLocations);
  const continents = useLocalizedContinents();
  const loading = useShippingStore((s) => s.loading);
  const fetchShipping = useShippingStore((s) => s.fetchShipping);
  const createZone = useShippingStore((s) => s.createZone);
  const updateZone = useShippingStore((s) => s.updateZone);
  const setZoneLocations = useShippingStore((s) => s.setZoneLocations);
  const addZoneMethod = useShippingStore((s) => s.addZoneMethod);
  const updateZoneMethod = useShippingStore((s) => s.updateZoneMethod);
  const deleteZoneMethod = useShippingStore((s) => s.deleteZoneMethod);

  const currencySymbol = useSettingsStore((s) => s.currencySymbol);

  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | undefined>(undefined);

  useEffect(() => {
    if (continents.length === 0 || zones.length === 0) {
      fetchShipping();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const zone =
    mode === 'edit' && zoneId !== undefined
      ? zones.find((z) => z.id === zoneId)
      : undefined;
  const methods = zone ? zoneMethods[zone.id] ?? EMPTY_METHODS : EMPTY_METHODS;
  const locations = zone ? zoneLocations[zone.id] ?? EMPTY_LOCATIONS : EMPTY_LOCATIONS;

  const seed = useMemo(
    () => deriveCustomShippingFields(zone, methods, locations),
    [zone, methods, locations],
  );

  const goBack = (): void => {
    navigate('/shipping');
  };

  const saveCreate = async (fields: CustomShippingFormFields): Promise<boolean> => {
    const created = await createZone(fields.name.trim());
    if (!created) {
      return false;
    }
    if (!(await setZoneLocations(created.id, removeAllRegionsLocation(fields.locations)))) {
      return false;
    }
    for (const [index, option] of fields.methods.entries()) {
      const method = await addZoneMethod(created.id, option.methodType);
      if (!method) {
        return false;
      }
      // Persist each method's position (its list index) so the arranged order sticks.
      const ok = await updateZoneMethod(created.id, method.instance_id, {
        settings: buildMethodSettings(option),
        enabled: option.data.enabled,
        sortOrder: index,
      });
      if (!ok) {
        return false;
      }
    }
    return true;
  };

  const saveEdit = async (
    id: string,
    fields: CustomShippingFormFields,
  ): Promise<boolean> => {
    if (!(await updateZone(id, fields.name.trim()))) {
      return false;
    }
    if (!(await setZoneLocations(id, removeAllRegionsLocation(fields.locations)))) {
      return false;
    }
    const ops = diffMethods(seed.methods, fields.methods);
    for (const instanceId of ops.toDelete) {
      if (!(await deleteZoneMethod(id, instanceId))) {
        return false;
      }
    }
    for (const op of ops.toUpdate) {
      if (!(await updateZoneMethod(id, op.instanceId, {
        settings: op.settings,
        enabled: op.enabled,
        sortOrder: op.sortOrder,
      }))) {
        return false;
      }
    }
    for (const op of ops.toAdd) {
      const method = await addZoneMethod(id, op.methodType);
      if (!method) {
        return false;
      }
      if (!(await updateZoneMethod(id, method.instance_id, {
        settings: op.settings,
        enabled: op.enabled,
        sortOrder: op.sortOrder,
      }))) {
        return false;
      }
    }
    return true;
  };

  const handleSubmit = async (fields: CustomShippingFormFields): Promise<void> => {
    setSaving(true);
    setSaveError(undefined);
    const ok =
      mode === 'edit' && zoneId !== undefined
        ? await saveEdit(zoneId, fields)
        : await saveCreate(fields);
    setSaving(false);
    if (!ok) {
      setSaveError(intl.formatMessage(adminMessages.shippingZoneSaveError));
      notifyError(intl.formatMessage(adminMessages.toastError));
      return;
    }
    notifySuccess(intl.formatMessage(adminMessages.toastSaved));
    goBack();
  };

  const seedReady = mode === 'create' || zone !== undefined;

  if (!seedReady) {
    if (loading || zones.length === 0) {
      return (
        <div className="gv-flex gv-items-center gv-justify-center gv-h-full gv-w-full">
          <Loader>
            <FormattedMessage {...adminMessages.shippingLoading} />
          </Loader>
        </div>
      );
    }
    return (
      <EmptyState
        title={intl.formatMessage(adminMessages.shippingZoneLoadErrorTitle)}
        description={intl.formatMessage(adminMessages.shippingZoneLoadErrorDescription)}
        action={{
          label: intl.formatMessage(adminMessages.shippingTitle),
          onClick: goBack,
        }}
      />
    );
  }

  return (
    <CustomShippingForm
      mode={mode}
      seed={seed}
      continents={continents}
      currencySymbol={currencySymbol}
      saving={saving}
      saveError={saveError}
      onSubmit={handleSubmit}
      onCancel={goBack}
    />
  );
}
